import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { motion, useAnimation, type PanInfo } from 'framer-motion'
import type { ComponentType } from '@/types/schemas/workspace'
import type { PanelComponent, WindowConfig } from '../types'
import { recordChartOpenIfChartTickerChanged } from '../record-chart-open'
import { PanelHeader } from './panel-header'
import { PanelWrapper } from './panel-wrapper'
import { COMPONENT_META } from './add-panel-menu-constants'
import { MobileWorkspaceContext, setGlobalSelectTicker } from './mobile-workspace-hooks'
import { useChatStore } from '@/stores/chat-store'
import { resolvePanelTitle } from '../panel-title'
import { useRolledOutPanelTypes } from '../panel-rollout'

// ─── Panel Configuration ───

const MOBILE_DASHBOARD_TYPES: ComponentType[] = [
  'scanner',
  'key-stats',
  'chart',
  'news',
  'ticker-stats',
  'notebook',
  'calendar',
  'watchlist',
  'multi-scanner',
]

function makePanelId(type: ComponentType): string {
  return `mobile_${type}`
}

// ─── Props ───

interface MobileWorkspaceProps {
  registry: Record<ComponentType, PanelComponent>
  symbols: string[]
  onSymbolsChange?: (symbols: string[]) => void
  /** Ref populated with the selectTicker function so the parent can invoke it */
  selectTickerRef?: React.MutableRefObject<((ticker: string) => void) | null>
  onExportPanel?: (panelId: string, config: WindowConfig) => void
  children?: ReactNode
}

// ─── Tab Bar ───

function MobileTabBar({
  types,
  activeIndex,
  onSelectTab,
}: {
  types: ComponentType[]
  activeIndex: number
  onSelectTab: (index: number) => void
}) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  // Auto-scroll active tab into view
  useEffect(() => {
    const el = tabRefs.current[activeIndex]
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [activeIndex])

  return (
    <div
      className="flex shrink-0 items-center gap-1 overflow-x-auto border-t border-border px-2 scrollbar-none"
      style={{ height: 56, minHeight: 56 }}
    >
      {types.map((type, i) => {
        const meta = COMPONENT_META[type]
        const Icon = meta?.icon
        const isActive = i === activeIndex

        return (
          <button
            key={type}
            ref={(el) => {
              tabRefs.current[i] = el
            }}
            onClick={() => onSelectTab(i)}
            className={
              'flex flex-col items-center justify-center rounded py-1 text-[10px] leading-tight transition-colors shrink-0 ' +
              (isActive ? 'text-primary bg-accent' : 'text-muted-foreground')
            }
            style={{ width: 64 }}
          >
            {Icon && <Icon className="mb-0.5 h-5 w-5" />}
            <span className="w-full truncate text-center">{meta?.label ?? type}</span>
          </button>
        )
      })}
    </div>
  )
}

// ─── Mobile Workspace ───

export function MobileWorkspace({
  registry,
  symbols,
  onSymbolsChange,
  selectTickerRef: parentSelectTickerRef,
  onExportPanel,
  children,
}: MobileWorkspaceProps) {
  // Tabs follow the rollout flags; configs cover every type, so a flag that
  // lands after first render (it reads off while loading) finds its tab ready.
  const types = useRolledOutPanelTypes(MOBILE_DASHBOARD_TYPES)
  const N = types.length

  // ─── Window configs (all onChange-linked, shared symbolIndex) ───
  const [windowConfigs, setWindowConfigs] = useState<Record<string, WindowConfig>>(() => {
    const configs: Record<string, WindowConfig> = {}
    for (const type of MOBILE_DASHBOARD_TYPES) {
      configs[makePanelId(type)] = { symbolIndex: 0, componentType: type, linkMode: 'onChange' }
    }
    return configs
  })

  // ─── Focus date for chart rewind ───
  const [focusDate, setFocusDate] = useState<string | null>(null)
  // Which component type published the current date. Panels gate on it (see
  // `followsFocusBroadcast`) so a source only drives the panels it should.
  const [focusDateSource, setFocusDateSource] = useState<ComponentType | null>(null)
  const handleFocusDate = useCallback((d: string | null, source: ComponentType) => {
    setFocusDate(d)
    setFocusDateSource(d ? source : null)
  }, [])

  // ─── Panel action handlers ───
  const panelActionHandlersRef = useRef<Record<string, (actionId: string) => void>>({})
  const [panelActionOverrides, setPanelActionOverrides] = useState<Record<string, string[]>>({})
  const [panelActionBadges, setPanelActionBadges] = useState<
    Record<string, Record<string, number>>
  >({})

  // ─── Symbol handling ───

  // Last rendered configs, so the handler can record activation outside the
  // state updater (React may run updaters during render).
  const windowConfigsRef = useRef(windowConfigs)
  windowConfigsRef.current = windowConfigs

  const handleSymbolChange = useCallback((_windowId: string, symbolIndex: number) => {
    // Update ALL configs to same symbolIndex (all onChange-linked)
    const linkAll = (configs: Record<string, WindowConfig>) => {
      const next = { ...configs }
      for (const id of Object.keys(next)) {
        next[id] = { ...next[id], symbolIndex, tickerCleared: false }
      }
      return next
    }
    setWindowConfigs(linkAll)
    // Only user ticker picks reach here.
    const prev = windowConfigsRef.current
    recordChartOpenIfChartTickerChanged(prev, linkAll(prev))
  }, [])

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const controls = useAnimation()

  const handleSelectTicker = useCallback(
    (_windowId: string, ticker: string) => {
      let idx = symbols.findIndex((s) => s.toLowerCase() === ticker.toLowerCase())

      if (idx === -1) {
        idx = symbols.length
        onSymbolsChange?.([...symbols, ticker.toUpperCase()])
      }

      setFocusDate(null)
      handleSymbolChange('', idx)

      // Auto-switch to chart tab
      const chartIndex = types.indexOf('chart')
      if (chartIndex >= 0) {
        setActiveTabIndex(chartIndex)
        controls.start({ x: `${-chartIndex * (100 / N)}%` })
      }
    },
    [symbols, onSymbolsChange, handleSymbolChange, controls, types, N],
  )

  // ─── Stable ref for selectTicker ───
  const selectTickerFnRef = useRef(handleSelectTicker)
  selectTickerFnRef.current = handleSelectTicker

  // ─── Lazy mounting ───
  const [visitedTabs, setVisitedTabs] = useState<Set<number>>(() => new Set([0]))

  useEffect(() => {
    setVisitedTabs((prev) => {
      if (prev.has(activeTabIndex)) return prev
      const next = new Set(prev)
      next.add(activeTabIndex)
      return next
    })
  }, [activeTabIndex])

  // ─── Swipe navigation ───

  const handleDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      const threshold = 0.2
      const velocityThreshold = 500
      const panelWidth = window.innerWidth

      let nextIndex = activeTabIndex

      if (info.offset.x < -panelWidth * threshold || info.velocity.x < -velocityThreshold) {
        nextIndex = Math.min(activeTabIndex + 1, N - 1)
      } else if (info.offset.x > panelWidth * threshold || info.velocity.x > velocityThreshold) {
        nextIndex = Math.max(activeTabIndex - 1, 0)
      }

      setActiveTabIndex(nextIndex)
      controls.start({ x: `${-nextIndex * (100 / N)}%` })
    },
    [activeTabIndex, N, controls],
  )

  // Sync animation when activeTabIndex changes (e.g. from tab bar click)
  useEffect(() => {
    controls.start({ x: `${-activeTabIndex * (100 / N)}%` })
  }, [activeTabIndex, N, controls])

  // ─── Handle clear ticker ───
  const handleClearTicker = useCallback((windowId: string) => {
    setWindowConfigs((prev) => {
      const cfg = prev[windowId]
      if (!cfg) return prev
      return { ...prev, [windowId]: { ...cfg, tickerCleared: true } }
    })
  }, [])

  // ─── Memoize the select ticker function for context ───
  const selectTicker = useMemo(() => {
    return (ticker: string) => {
      selectTickerFnRef.current('', ticker)
    }
  }, [])

  // Expose selectTicker to parent via ref
  useEffect(() => {
    if (parentSelectTickerRef) parentSelectTickerRef.current = selectTicker
    return () => {
      if (parentSelectTickerRef) parentSelectTickerRef.current = null
    }
  }, [selectTicker, parentSelectTickerRef])

  // Register globally so TopBar (outside provider tree) can access it
  useEffect(() => {
    setGlobalSelectTicker(selectTicker)
    return () => setGlobalSelectTicker(null)
  }, [selectTicker])

  return (
    <MobileWorkspaceContext value={{ selectTicker }}>
      <div className="flex h-full flex-col">
        {children}

        {/* Swipeable panel strip — small padding so edges are grabbable for swiping */}
        <div className="relative flex-1 overflow-hidden bg-background p-2">
          <div className="h-full rounded-sm border border-border bg-card overflow-hidden">
            <motion.div
              className="flex h-full"
              style={{ width: `${N * 100}%` }}
              drag="x"
              dragDirectionLock
              dragElastic={0.15}
              dragMomentum={false}
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={handleDragEnd}
              animate={controls}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {types.map((type, i) => {
                const panelId = makePanelId(type)
                const config = windowConfigs[panelId]
                const PanelComp = registry[type]
                const isVisited = visitedTabs.has(i)

                return (
                  <div key={type} className="flex flex-col" style={{ width: `${100 / N}%` }}>
                    {isVisited ? (
                      <>
                        {/* Panel header */}
                        <div
                          className="flex w-full items-center shrink-0"
                          style={{ height: 30, minHeight: 30 }}
                        >
                          <PanelHeader
                            panelId={panelId}
                            windowConfig={config}
                            symbols={symbols}
                            onSymbolChange={(idx) => handleSymbolChange(panelId, idx)}
                            onLinkModeChange={() => {}}
                            onDelete={() => {}}
                            onExport={
                              onExportPanel ? () => onExportPanel(panelId, config) : undefined
                            }
                            onAction={(actionId) =>
                              panelActionHandlersRef.current[panelId]?.(actionId)
                            }
                            actionOverrides={panelActionOverrides[panelId]}
                            actionBadges={panelActionBadges[panelId]}
                            locked
                            focusDate={focusDate}
                            focusDateSource={focusDateSource}
                            onResetFocusDate={() => {
                              setFocusDate(null)
                              setFocusDateSource(null)
                            }}
                            tickerCleared={config?.tickerCleared}
                            onClearTicker={
                              type === 'news' ? () => handleClearTicker(panelId) : undefined
                            }
                            onAskAI={() =>
                              useChatStore.getState().openWithMention({
                                panelId,
                                componentType: type,
                                title: resolvePanelTitle(panelId),
                              })
                            }
                          />
                        </div>

                        {/* Panel content */}
                        <div className="flex-1 overflow-hidden">
                          <PanelWrapper
                            panelId={panelId}
                            isActive={i === activeTabIndex}
                            onActivate={() => {}}
                          >
                            <PanelComp
                              panelId={panelId}
                              windowConfig={config}
                              symbols={symbols}
                              focusDate={focusDate}
                              focusDateSource={focusDateSource}
                              onFocusDate={(d) => handleFocusDate(d, type)}
                              onSelectTicker={(t) => handleSelectTicker(panelId, t)}
                              onRegisterToolbarHandler={(handler) => {
                                panelActionHandlersRef.current[panelId] = handler
                              }}
                              onRegisterPanelActions={(actionIds) => {
                                setPanelActionOverrides((prev) => ({
                                  ...prev,
                                  [panelId]: actionIds,
                                }))
                              }}
                              onRegisterActionBadges={(badges) => {
                                setPanelActionBadges((prev) => ({ ...prev, [panelId]: badges }))
                              }}
                            />
                          </PanelWrapper>
                        </div>
                      </>
                    ) : (
                      // Lightweight placeholder for unvisited tabs
                      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                        {COMPONENT_META[type]?.label ?? type}
                      </div>
                    )}
                  </div>
                )
              })}
            </motion.div>
          </div>
        </div>

        {/* Bottom tab bar */}
        <MobileTabBar types={types} activeIndex={activeTabIndex} onSelectTab={setActiveTabIndex} />
      </div>
    </MobileWorkspaceContext>
  )
}
